import { useCallback, useMemo, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import type {
  ClipCompilationPageProps,
  CompilationFilters,
  CompilationListItem,
  RequestCompilationFormValues,
} from "@visioni/a3s-media/app";
import { useCompilationsList, useRequestCompilation } from "@visioni/a3s-media/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { toISODateString } from "@visioni/a3s-codecs";
import type { SiteListItem } from "@visioni/a3s-provisioning/client-messages";
import { useSites } from "@src/features/cameras/hooks";
import { useCameras } from "@src/services/api/hooks";
import type { CameraApiResponse } from "@src/features/cameras";
import { getCameraKeyString, formatCameraLabel } from "@src/utils/key-parser";
import { downloadVideo, handleDownloadError } from "@src/features/clips";
import { useIsSupportUser } from "@src/features/tenants";
import { showErrorToast } from "@src/utils/toast";
import { combineDateAndTime } from "./combine-date-and-time";

const DEFAULT_FILTERS: CompilationFilters = {
  dateRange: { start: null, end: null },
  status: null,
  siteId: null,
  cameraId: null,
};

const getSiteId = (s: SiteListItem): string => SiteId.toString(s.siteId);
const getSiteLabel = (s: SiteListItem): string => s.siteName;
const getCameraId = (c: CameraApiResponse): string => getCameraKeyString(c);
const getCameraLabel = (c: CameraApiResponse): string => c.nickname || formatCameraLabel(getCameraKeyString(c));

export interface UseClipCompilationPagePropsResult {
  readonly pageProps: ClipCompilationPageProps<SiteListItem, CameraApiResponse>;
  /** Request dialog state — the route renders `RequestCompilationForm` inside
   * a `DialogModal` controlled by these, since `ClipCompilationPage` itself
   * only exposes `onRequestCompilation` as a bare trigger. */
  readonly isRequestDialogOpen: boolean;
  readonly closeRequestDialog: () => void;
  readonly cameras: readonly CameraApiResponse[];
  readonly getCameraId: (camera: CameraApiResponse) => string;
  readonly getCameraLabel: (camera: CameraApiResponse) => string;
  readonly onRequestSubmit: (values: RequestCompilationFormValues) => void;
  readonly isSubmitting: boolean;
  readonly submitError: Error | null;
  /** Watch view state — the route swaps the list for `CompilationWatchView`
   * in place when set, rather than navigating (see that component's doc for why). */
  readonly watchingItem: CompilationListItem | null;
  readonly closeWatchView: () => void;
}

/**
 * Wires `ClipCompilationPage` to real data — `useCompilationsList`/
 * `useRequestCompilation` (`@visioni/a3s-media/app`), and `sites`/`cameras`
 * from this app's own existing sources (`useSites`, `useCameras`) rather than
 * a new a3s-media query. Mirrors `useActivityPageProps`'s shape.
 */
export function useClipCompilationPageProps(): UseClipCompilationPagePropsResult {
  const [filters, setFilters] = useState<CompilationFilters>(DEFAULT_FILTERS);
  const [isRequestDialogOpen, setRequestDialogOpen] = useState(false);
  const router = useRouter();
  // Only the id round-trips through the URL (not the signed URLs themselves —
  // see `CompilationWatchView`'s doc for why) so refreshing keeps the watch
  // view open; the actual item (with its signed URLs) is looked up from the
  // already-loaded list below once `items` is computed.
  const { watching: watchingRequestId } = useLocalSearchParams<{ watching?: string }>();
  const isSupportUser = useIsSupportUser();

  const { sites } = useSites();
  const { data: cameras } = useCameras();

  const cameraNames = useMemo(
    () => new Map((cameras ?? []).map((c) => [getCameraKeyString(c), getCameraLabel(c)])),
    [cameras],
  );

  const list = useCompilationsList({
    siteId: filters.siteId ?? undefined,
    cameraKey: filters.cameraId ?? undefined,
    dateRange: filters.dateRange.start || filters.dateRange.end ? filters.dateRange : undefined,
    cameraNames,
  });

  // Applied client-side against the mapped view model rather than passed as
  // the hook's server-side `status` option, because the row status enum still
  // isn't the wire enum 1:1: a `ready` job with no signed URL displays as
  // `expired`, which the endpoint has no notion of (see
  // `use-compilations-list.ts`). Every other row status now matches the wire
  // status exactly, so this can move server-side once `expired` is resolved
  // — either by filtering it out here or by the endpoint modelling it.
  const items = useMemo(
    () => (filters.status ? list.items.filter((item) => item.status === filters.status) : list.items),
    [list.items, filters.status],
  );

  // Not found until the matching page has loaded (e.g. straight after a
  // refresh, or the item sits behind more "Load more" pages) — the watch
  // view just doesn't open yet in that case rather than fetching by id.
  const watchingItem = useMemo(
    () => (watchingRequestId ? (items.find((item) => item.id === watchingRequestId) ?? null) : null),
    [items, watchingRequestId],
  );

  const { requestCompilation, isPending, error: submitError } = useRequestCompilation();

  const onRequestSubmit = useCallback(
    (values: RequestCompilationFormValues) => {
      const camera = cameras?.find((c) => getCameraKeyString(c) === values.cameraId);
      if (!camera || !values.startDate || !values.endDate) return;

      void requestCompilation({
        siteId: SiteId.fromString(camera.site.id),
        assetCameraKey: AssetCameraKey.fromString(getCameraKeyString(camera)),
        body: {
          startTime: toISODateString(combineDateAndTime(values.startDate, values.startTime)),
          endTime: toISODateString(combineDateAndTime(values.endDate, values.endTime)),
        },
      })
        .then(() => setRequestDialogOpen(false))
        .catch((error: unknown) => {
          // Dialog stays open so the user can retry without re-entering the form.
          showErrorToast({ error, description: "Could not request the compilation. Please try again." });
        });
    },
    [cameras, requestCompilation],
  );

  const onWatch = useCallback(
    (item: CompilationListItem) => {
      if (!item.videoUrl) return;
      router.setParams({ watching: item.id });
    },
    [router],
  );

  const onDownload = useCallback((item: CompilationListItem) => {
    if (!item.downloadUrl) return;
    void downloadVideo({ videoUrl: item.downloadUrl, timestamp: new Date() }).catch(handleDownloadError);
  }, []);

  return {
    pageProps: {
      items,
      isLoading: list.isLoading,
      error: list.error != null,
      hasNextPage: list.hasNextPage,
      isFetchingNextPage: list.isFetchingNextPage,
      onLoadMore: list.fetchNextPage,
      onRequestCompilation: isSupportUser ? () => setRequestDialogOpen(true) : undefined,
      onWatch,
      onDownload,
      filters,
      onFiltersChange: setFilters,
      sites: sites ?? [],
      cameras: cameras ?? [],
      getSiteLabel,
      getSiteId,
      getCameraLabel,
      getCameraId,
    },
    isRequestDialogOpen,
    closeRequestDialog: () => setRequestDialogOpen(false),
    cameras: cameras ?? [],
    getCameraId,
    getCameraLabel,
    onRequestSubmit,
    isSubmitting: isPending,
    submitError,
    watchingItem,
    closeWatchView: () => router.setParams({ watching: "" }),
  };
}
